/**
 * ChartCard — a card that flips between a chart view and a data table.
 *
 * Reusable wrapper for any SVG chart. The caller supplies:
 *   - chart: the chart ReactNode (front face)
 *   - table: the data table ReactNode (back face)
 *   - actions: optional slot for filter pills / controls (placed in the header)
 */
import { useState, type CSSProperties, type ReactNode } from 'react'
import { FaChartBar, FaTable } from 'react-icons/fa'

interface Props {
  title:    string
  chart:    ReactNode
  table:    ReactNode
  /** Optional header slot — e.g. filter toggles */
  actions?: ReactNode
  showTableLabel?: string
  showChartLabel?: string
}

export default function ChartCard({
  title,
  chart,
  table,
  actions,
  showTableLabel = 'Show table',
  showChartLabel = 'Show chart',
}: Props) {
  const [showTable, setShowTable] = useState(false)

  return (
    <div style={css.card}>
      <div style={css.header}>
        <span style={css.title}>{title}</span>
        <div style={css.controls}>
          {actions}
          <button
            style={css.flipBtn}
            onClick={() => setShowTable(v => !v)}
            title={showTable ? showChartLabel : showTableLabel}
            aria-pressed={showTable}
          >
            {showTable ? <FaChartBar size={18} aria-hidden="true" /> : <FaTable size={18} aria-hidden="true" />}
          </button>
        </div>
      </div>
      <div style={css.body}>
        {showTable ? table : chart}
      </div>
    </div>
  )
}

const css: Record<string, CSSProperties> = {
  card: {
    background:    '#fff',
    borderRadius:  '16px',
    border:        '1px solid #e8e0d5',
    overflow:      'hidden',
  },
  header: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    gap:            '12px',
    padding:        '14px 20px',
    borderBottom:   '1px solid #f0ece6',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 700,
    color:      '#2D3A2E',
    flexShrink: 0,
  },
  controls: {
    display:    'flex',
    alignItems: 'center',
    gap:        '8px',
    flexWrap:   'wrap' as const,
    justifyContent: 'flex-end',
  },
  flipBtn: {
    background:   'none',
    border:       '1px solid #e0d8d0',
    borderRadius: '8px',
    padding:      '5px 10px',
    cursor:       'pointer',
    display:      'flex',
    alignItems:   'center',
    justifyContent: 'center',
    minHeight:    '36px',
    minWidth:     '40px',
    flexShrink:   0,
  },
  body: {
    padding: '16px 20px',
  },
}
